"use client";

import type React from "react";
import { useState, useEffect } from "react";
import { BiArrowBack, BiSearch } from "react-icons/bi";
import { FaStar } from "react-icons/fa6";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getAllRidersStats, getRidersOverview } from "../../redux/thunk";

// Custom Components
const Card = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={`rounded-lg border bg-white text-card-foreground shadow-sm ${className}`}
    >
      {children}
    </div>
  );
};

const Badge = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <span
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${className}`}
    >
      {children}
    </span>
  );
};

const Input = ({
  className,
  ...props
}: React.InputHTMLAttributes<HTMLInputElement>) => (
  <input
    className={`px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent ${className}`}
    {...props}
  />
);

const ProgressBar = ({ 
  progress, 
  className = "" 
}: { 
  progress: number; 
  className?: string; 
}) => {
  return (
    <div className={`w-full bg-gray-200 rounded-full h-2 ${className}`}>
      <div
        className="bg-green-500 h-2 rounded-full transition-all duration-300"
        style={{ width: `${progress}%` }}
      ></div>
    </div>
  );
};

export const PerformanceMonitoring = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Statuses");
  
  // Redux selectors with fallbacks
  const logisticsState = useSelector((state: RootState) => state.logistics);
  const ridersPerformance = logisticsState?.allRidersStats?.data || [];
  const ridersOverview = logisticsState?.ridersOverview || null;
  const status = logisticsState?.status || "idle";
  const error = logisticsState?.error || null;
  const currentPage = logisticsState?.allRidersStats?.page || 1;
  const totalPages = logisticsState?.allRidersStats?.totalPages || 0;
  const totalRiders = logisticsState?.allRidersStats?.totalRiders || 0;
  
  // Fetch data on component mount
  useEffect(() => {
    dispatch(getRidersOverview());
    dispatch(getAllRidersStats({ 
      page: 1, 
      limit: 20
    }));
  }, [dispatch]);
  
  // Fetch performance data when search/filter changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      dispatch(getAllRidersStats({ 
        page: 1, 
        limit: 20
      }));
    }, 300); // Debounce search
    
    return () => clearTimeout(timeoutId);
  }, [dispatch, searchTerm, statusFilter]);
  
  // Loading state
  const isLoading = status === "loading";

  // KPI Data from ridersOverview
  const kpiData = ridersOverview ? [
    {
      title: "Active Riders",
      value: ridersOverview.activeRiders.toString(),
      target: "Total Active",
      percentage: parseFloat(Math.min(100, (ridersOverview.activeRiders + ridersOverview.pendingApproval) > 0 ? (ridersOverview.activeRiders / (ridersOverview.activeRiders + ridersOverview.pendingApproval)) * 100 : 0).toFixed(1)),
      status: "Active",
      statusColor: "bg-green-100 text-green-800",
    },
    {
      title: "External Riders",
      value: ridersOverview.externalRiders.toString(),
      target: "External Partners",
      percentage: parseFloat(Math.min(100, ridersOverview.activeRiders > 0 ? (ridersOverview.externalRiders / ridersOverview.activeRiders) * 100 : 0).toFixed(1)),
      status: "External",
      statusColor: "bg-blue-100 text-blue-800",
    },
    {
      title: "Internal Riders",
      value: ridersOverview.internalRiders.toString(),
      target: "Internal Staff",
      percentage: parseFloat(Math.min(100, ridersOverview.activeRiders > 0 ? (ridersOverview.internalRiders / ridersOverview.activeRiders) * 100 : 0).toFixed(1)),
      status: "Internal",
      statusColor: "bg-purple-100 text-purple-800",
    },
    {
      title: "Pending Approval",
      value: ridersOverview.pendingApproval.toString(),
      target: "Awaiting Review",
      percentage: parseFloat(Math.min(100, (ridersOverview.activeRiders + ridersOverview.pendingApproval) > 0 ? (ridersOverview.pendingApproval / (ridersOverview.activeRiders + ridersOverview.pendingApproval)) * 100 : 0).toFixed(1)),
      status: ridersOverview.pendingApproval > 0 ? "Pending" : "Clear",
      statusColor: ridersOverview.pendingApproval > 0 ? "bg-yellow-100 text-yellow-800" : "bg-green-100 text-green-800",
    },
  ] : [];

  // Mock data for weekly trends and issues until we have real endpoints
  const weeklyTrends = [
    { day: "Monday", orders: Math.floor(Math.random() * 100) + 150, time: "25mins", progress: parseFloat((Math.random() * 20 + 80).toFixed(1)) },
    { day: "Tuesday", orders: Math.floor(Math.random() * 100) + 150, time: "23mins", progress: parseFloat((Math.random() * 20 + 80).toFixed(1)) },
    { day: "Wednesday", orders: Math.floor(Math.random() * 100) + 150, time: "27mins", progress: parseFloat((Math.random() * 20 + 80).toFixed(1)) },
    { day: "Thursday", orders: Math.floor(Math.random() * 100) + 150, time: "22mins", progress: parseFloat((Math.random() * 20 + 80).toFixed(1)) },
    { day: "Friday", orders: Math.floor(Math.random() * 100) + 150, time: "26mins", progress: parseFloat((Math.random() * 20 + 80).toFixed(1)) },
    { day: "Saturday", orders: Math.floor(Math.random() * 100) + 200, time: "28mins", progress: parseFloat((Math.random() * 20 + 80).toFixed(1)) },
    { day: "Sunday", orders: Math.floor(Math.random() * 100) + 180, time: "24mins", progress: parseFloat((Math.random() * 20 + 80).toFixed(1)) },
  ];
  
  const riderIssues = ridersPerformance.length > 0 ? ridersPerformance
    .filter(rider => rider.accountStatus === 'suspended')
    .slice(0, 3)
    .map(rider => ({
      rider: rider.fullName,
      deliveries: Math.floor(Math.random() * 30) + 10,
      issueCount: Math.floor(Math.random() * 5) + 1,
    })) : [];
    
  const locationTrends = [
    {
      location: "Mainland",
      orders: Math.floor(Math.random() * 200) + 600,
      time: "25mins",
      riders: Math.floor(Math.random() * 20) + 25,
      rating: parseFloat((4.2 + Math.random() * 0.6).toFixed(1)),
      progress: parseFloat((Math.random() * 20 + 80).toFixed(1)),
    },
    {
      location: "Island",
      orders: Math.floor(Math.random() * 200) + 400,
      time: "28mins",
      riders: Math.floor(Math.random() * 15) + 20,
      rating: parseFloat((4.0 + Math.random() * 0.8).toFixed(1)),
      progress: parseFloat((Math.random() * 25 + 75).toFixed(1)),
    },
  ];

  // Filter riders based on search term and status
  const filteredRiders = ridersPerformance.filter((rider) => {
    const matchesSearch = rider.fullName.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "All Statuses" || rider.accountStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });
  
  // Top Performers Data from API - filtered and sorted by performance
  const topPerformers = filteredRiders
    .filter(rider => rider.totalEarnings > 0 && rider.averageRating)
    .sort((a, b) => {
      // Sort by earnings first, then by rating
      if (b.totalEarnings !== a.totalEarnings) return b.totalEarnings - a.totalEarnings;
      return parseFloat(b.averageRating || '0') - parseFloat(a.averageRating || '0');
    })
    .slice(0, 5) // Take top 5 performers
    .map(rider => ({
      name: rider.fullName,
      deliveries: Math.floor(Math.random() * 50) + 10, // Mock deliveries for now
      rating: parseFloat(rider.averageRating || '0'),
      earnings: `₦${rider.totalEarnings.toLocaleString()}.00`,
    }));

  return (
    <div className="flex-1 flex flex-col">
      {/* Header */}
      <header className="mb-6 h-16 border-b border-gray-200 bg-white flex items-center justify-between">
        <div className="flex items-center gap-4">
          <BiArrowBack 
            className="w-6 h-6 text-gray-600 cursor-pointer" 
            onClick={() => window.history.back()} 
          />
          <div>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Logistics</span>
              <span>›</span>
              <span>Performance Monitoring</span>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              Performance Monitoring
            </h2>
            <p className="text-sm text-gray-500">
              Track KPIs, analyze trends, and monitor rider performance
            </p>
          </div>
        </div>
      </header>

      <div className="space-y-6">
        {/* Error Display */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative" role="alert">
            <span className="block sm:inline">Error: {error}</span>
          </div>
        )}
        
        {/* KPI Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {isLoading ? (
            // Loading skeleton for KPI cards
            Array.from({ length: 4 }).map((_, index) => (
              <Card key={index} className="p-6">
                <div className="flex flex-col animate-pulse">
                  <div className="flex items-start justify-between mb-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                    <div className="h-6 bg-gray-200 rounded w-16"></div>
                  </div>
                  <div className="mb-2">
                    <div className="h-8 bg-gray-200 rounded w-20"></div>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="h-3 bg-gray-200 rounded w-20"></div>
                    <div className="h-3 bg-gray-200 rounded w-8"></div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded"></div>
                </div>
              </Card>
            ))
          ) : (
            kpiData.map((kpi, index) => (
              <Card key={index} className="p-6">
                <div className="flex flex-col">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="text-sm font-medium text-gray-600">
                      {kpi.title}
                    </h3>
                    <Badge className={kpi.statusColor}>
                      {kpi.status}
                    </Badge>
                  </div>
                  <div className="mb-2">
                    <div className="text-2xl font-bold text-gray-900">
                      {kpi.value}
                    </div>
                  </div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs text-gray-500">{kpi.target}</span>
                    <span className="text-xs text-gray-500">{kpi.percentage}%</span>
                  </div>
                  <ProgressBar progress={kpi.percentage} />
                </div>
              </Card>
            ))
          )}
        </div>

        {/* Search Bar */}
        <div className="flex items-center gap-6">
          <div className="relative flex-1 max-w-md">
            <BiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Search Rider by name"
              className="pl-10 h-10 w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="h-10 rounded-md border border-input px-3 py-2 text-sm"
            >
              <option>All Statuses</option>
              <option>active</option>
              <option>inactive</option>
              <option>suspended</option>
              <option>pending</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Weekly Trends */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Weekly Trends
            </h3>
            <div className="space-y-4">
              {isLoading ? (
                // Loading skeleton for weekly trends
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="flex items-center justify-between animate-pulse">
                    <div className="flex items-center gap-4">
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-2 bg-gray-200 rounded-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-16"></div>
                      <div className="w-20 h-2 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                ))
              ) : (
                weeklyTrends.map((trend, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-sm text-gray-600 w-16">{trend.day}</span>
                      <span className="text-sm text-gray-900">{trend.orders} Orders</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-2 bg-gray-200 rounded-full relative">
                        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-green-500 rounded-full border-2 border-white"></div>
                      </div>
                      <span className="text-sm text-gray-600 w-16">{trend.time}</span>
                      <ProgressBar progress={trend.progress} className="w-20" />
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Issues Section */}
            <div className="mt-8">
              <h4 className="text-md font-semibold text-gray-900 mb-4">Issues</h4>
              {isLoading ? (
                Array.from({ length: 2 }).map((_, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-t animate-pulse">
                    <div>
                      <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-20"></div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-16"></div>
                  </div>
                ))
              ) : (
                riderIssues.map((issue, index) => (
                  <div key={index} className="flex items-center justify-between py-3 border-t">
                    <div>
                      <div className="font-medium text-gray-900">{issue.rider}</div>
                      <div className="text-sm text-gray-500">({issue.deliveries} deliveries)</div>
                    </div>
                    <div className="text-red-600 font-medium">{issue.issueCount} Issues</div>
                  </div>
                ))
              )}
            </div>
          </Card>

          {/* Top Performers */}
          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Top Performers
            </h3>
            <p className="text-sm text-gray-500 mb-4">Top Performers This Week</p>
            <div className="space-y-4">
              {isLoading ? (
                // Loading skeleton for top performers
                Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="flex items-center justify-between animate-pulse">
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-32 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-24"></div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-6 bg-gray-200 rounded w-12"></div>
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                    </div>
                  </div>
                ))
              ) : topPerformers.length > 0 ? (
                topPerformers.map((performer, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">{performer.name}</div>
                      <div className="text-sm text-gray-500">({performer.deliveries} deliveries)</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-1 bg-orange-400 text-white px-2 py-1 rounded text-sm">
                        <span>{performer.rating.toFixed(1)}</span>
                        <FaStar className="w-3 h-3" />
                      </div>
                      <div className="font-medium text-gray-900 text-right">
                        {performer.earnings}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-gray-500 py-4">
                  No performance data available
                </div>
              )}
            </div>
          </Card>
        </div>

        {/* Weekly Trends by Location */}
        <Card className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Weekly Trends by Location
          </h3>
          <div className="space-y-6">
            {isLoading ? (
              // Loading skeleton for location trends
              Array.from({ length: 2 }).map((_, index) => (
                <div key={index} className="animate-pulse">
                  <div className="flex items-center justify-between mb-2">
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                  <div className="h-2 bg-gray-200 rounded mb-3"></div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-6">
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                      <div className="h-3 bg-gray-200 rounded w-12"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="h-3 bg-gray-200 rounded w-8"></div>
                      <div className="w-3 h-3 bg-gray-200 rounded"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : locationTrends.length > 0 ? (
              locationTrends.map((location, index) => (
                <div key={index}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-medium text-gray-900">{location.location}</h4>
                  </div>
                  <ProgressBar progress={location.progress} className="mb-3" />
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-6">
                      <span className="text-gray-600">{location.orders} Orders</span>
                      <span className="text-gray-600">{location.time}</span>
                      <span className="text-gray-600">{location.riders} Riders</span>
                    </div>
                    <div className="flex items-center gap-1 text-orange-500">
                      <span>{location.rating.toFixed(1)}</span>
                      <FaStar className="w-3 h-3" />
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-gray-500 py-4">
                No location trends data available
              </div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};